import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { environment } from 'mapfre-auditmap/src/environments/environment';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { map, mergeMap } from "rxjs/operators";
import { FileService, CacheService, IActivityBO, IExternalAuditBO, ITypeExternalAuditBO } from '@mapfre-tech/amap3';


@Injectable({
  providedIn: 'root'
})
export class AuditService {

  className = 'AuditService';

  constructor(private http: HttpClient, private fileService: FileService, private cacheService: CacheService) { }


  getAllAudits(cache = true, cacheKey = "typeExternalAudit"): Observable<ITypeExternalAuditBO[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeExternalAuditBO[]>;
    } else {
      const observable = this.http.get<ITypeExternalAuditBO[]>(environment.base_URL + "/external-audit-types").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  newAudit(audit: ITypeExternalAuditBO) {
    return this.http.post(environment.base_URL + "/external-audit-types", audit)
  }

  updateAudit(audit: ITypeExternalAuditBO, id: number) {
    return this.http.put(environment.base_URL + `/external-audit-types/${id}`, audit)
  }

  getAllExternalAudits(): Observable<IExternalAuditBO[]> {
    return this.http.get<IExternalAuditBO[]>(environment.base_URL + "/external-audits")
  }

  newExternalAudit(audit: IExternalAuditBO): Observable<IExternalAuditBO[]> {
    return this.http.post<IExternalAuditBO[]>(environment.base_URL + "/external-audits", audit)
  }

  updateExternalAudit(audit: IExternalAuditBO, id: number) {
    return this.http.put(environment.base_URL + `/external-audits/${id}`, audit)
  }

  deleteExternalAudit(id: number) {
    return this.http.delete(environment.base_URL + `/external-audits/${id}`)
  }

  getExternalAudit(id: number): Observable<IExternalAuditBO[]> {
    const params: any = {
      externalAuditId: id
    }
    return this.http.get<IExternalAuditBO[]>(environment.base_URL + "/external-audits", { params });
  }

  addExternalAuditWithFiles(externalAudit: IExternalAuditBO): Observable<IExternalAuditBO[]> {
    // GET INVOICE FILES    
    const invoiceFiles = this.getFiles(externalAudit.invoices);
    // GET REPORTS FILES
    const reportFiles = this.getFiles(externalAudit.reports);
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(invoiceFiles) || !_.isEmpty(reportFiles)) {
      return this.newExternalAudit(externalAudit).pipe(mergeMap((res: any) => {
        const observables: any = [];
        if (res.invoices && res.invoices.length > 0) {
          _.forEach(res.invoices, invoice => {
            //GET FILE
            let file = null;
            if (invoice.uuid) {
              file = _.find(invoiceFiles, file => file.uuid === invoice.uuid);
            }
            else {
              file = _.find(invoiceFiles, file => file.id === invoice.idExternalAuditInvoicePK);
            }
            if (file) {
              const files = [file];
              observables.push(_.map(files, file => this.fileService.upload(file, 'invoice', invoice.idExternalAuditInvoicePK)));
            }
          });
        }
        if (res.reports && res.reports.length > 0) {
          _.forEach(res.reports, report => {
            //GET FILE
            let file = null;
            if (report.uuid) {
              file = _.find(reportFiles, file => file.uuid === report.uuid);
            }
            else {
              file = _.find(reportFiles, file => file.id === report.idExternalReportPK);
            }
            if (file) {
              const files = [file];
              observables.push(_.map(files, file => this.fileService.upload(file, 'external-report', report.idExternalReportPK)));
            }
          });
        }
        const result = _.reduce(observables, (prev: any, next: any) => _.concat(prev, next));
        return forkJoin(result).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.newExternalAudit(externalAudit)
    }
  }

  getFiles(items: any) {
    const files: any[] = [];
    _.forEach(items, item => {
      item.fileNames = item.fileNames ? _.toArray(_.get(item, 'fileNames')) : [];
      const filesToUpload: any = _.remove(_.get(item, 'fileNames'), (f) => f instanceof File);
      if (filesToUpload && filesToUpload.length > 0) {
        _.forEach(filesToUpload, file => {
          if (item.uuid) {
            file.uuid = item.uuid;
          }
          if (item.idExternalAuditInvoicePK) {
            file.id = item.idExternalAuditInvoicePK;
          }

          if (item.idExternalReportPK) {
            file.id = item.idExternalReportPK;
          }
          files.push(file);
        })
      }
    });
    return files;
  }


  addExternalAuditListWithFiles(activities: IExternalAuditBO[]): Observable<IExternalAuditBO[][]> {
    const observables = _.map(activities, (activity) => this.addExternalAuditWithFiles(activity));
    return forkJoin(observables);
  }

}
